import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import { Grid2, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	CATALOG_GRADE_INITIAL_VALUES,
	CatalogGrade,
} from "@/shared/models/CatalogGrade";
import {
	deleteCatalogGrade,
	upsertCatalogGrade,
} from "@/shared/repositories/CatalogGradeRepository";

type Props = {
	grade: CatalogGrade | null;
	modal: "create" | "update" | "delete" | "";
	onHide: (shouldUpdate?: boolean) => void;
};

export const UpsertDeleteModal = ({ grade, modal, onHide }: Props) => {
	const [title, setTitle] = useState<string>("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [error, setError] = useState("");

	const successMessage = `The grade was ${modal}d successfully`;

	const { control, reset, handleSubmit } = useForm<CatalogGrade>({});

	const queryClient = useQueryClient();

	const deleteMutation = useMutation({
		mutationFn: (gradeId: number) => deleteCatalogGrade(gradeId),
		onSuccess: async (response) => {
			if (response.ok) {
				await queryClient.invalidateQueries({ queryKey: ["catalogGrades"] });
				setNotificationMessage(successMessage);
				onHide(true);
			} else {
				setError(response.message);
			}
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const upsertMutation = useMutation({
		mutationFn: (data: CatalogGrade) => upsertCatalogGrade(data),
		onSuccess: async (response) => {
			if (response.ok) {
				await queryClient.invalidateQueries({ queryKey: ["catalogGrades"] });
				setNotificationMessage(successMessage);
				onHide(true);
			} else {
				setError(response.message);
			}
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const onSubmit: SubmitHandler<CatalogGrade> = async (data: CatalogGrade) => {
		if (modal === "delete" && data.GradeId) {
			deleteMutation.mutate(data.GradeId);
		} else {
			upsertMutation.mutate(data);
		}
	};

	useEffect(() => {
		if (modal !== "") {
			reset(
				modal === "create"
					? CATALOG_GRADE_INITIAL_VALUES
					: grade || CATALOG_GRADE_INITIAL_VALUES,
			);
			setTitle(
				modal === "create" ? "CREATE" : modal === "update" ? "EDIT" : "DELETE",
			);
		}
	}, [modal, grade, reset]);

	return (
		<>
			{(modal === "create" || modal === "update" || modal === "delete") && (
				<HelmetDexteel
					title={`${
						modal === "create"
							? "New"
							: `${modal === "update" ? "Edit" : "Delete"}`
					} Grade - Grades`}
				/>
			)}
			<MesfModal
				id="modal-grade"
				title={`${title} GRADE`}
				open={modal !== ""}
				handleClose={() => onHide(false)}
				maxWidth="sm"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "15px 30px" }}>
						<Grid2 container spacing={2}>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Controller
									name="GradeName"
									control={control}
									rules={{
										required: "Name is required",
									}}
									render={({ field, fieldState: { error } }) => (
										<TextField
											label="Name"
											variant="outlined"
											value={field.value || ""}
											onChange={field.onChange}
											error={!!error}
											helperText={error?.message}
											fullWidth
											autoComplete="off"
											size="small"
											disabled={modal === "delete"}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={
								deleteMutation.isPending || upsertMutation.isPending
							}
							onHide={onHide}
							buttonLabel={modal === "delete" ? "Delete" : "Save"}
							buttonColor={modal === "delete" ? "secondary" : "primary"}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
